import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { APPLICATION_KEYS } from "./constants/appKeys";
import { registerPushNotificationInteraction } from "./endpoints/api";

// Check if Firebase configuration is available
const isFirebaseConfigured = () => {
  return !!(
    process.env.REACT_APP_FIREBASE_API_KEY &&
    process.env.REACT_APP_FIREBASE_PROJECT_ID &&
    process.env.REACT_APP_FIREBASE_APP_ID
  );
};

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase only if configured
let app;
let messaging;
let analytics;

if (isFirebaseConfigured()) {
  try {
    app = initializeApp(firebaseConfig);
    console.log('Firebase initialized successfully');
    
    // Initialize Analytics if supported and measurement ID is provided
    if (process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) {
      isAnalyticsSupported().then((analyticsSupported) => {
        if (analyticsSupported) {
          // Configure analytics with debug mode if enabled
          const analyticsConfig = {
            'site_section': 'web_app',
          };
          
          // Enable debug mode in development or when explicitly set
          const isDebugMode = process.env.NODE_ENV === 'development' || 
                             process.env.REACT_APP_GA_DEBUG_MODE === 'true';
          
          if (isDebugMode) {
            analyticsConfig.debug_mode = true;
            console.log('[Analytics] Firebase Analytics initialized with debug mode enabled');
          } else {
            console.log('[Analytics] Firebase Analytics initialized');
          }
          
          analytics = getAnalytics(app); // Initialize analytics instance

          // Set initial configuration
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('config', process.env.REACT_APP_FIREBASE_MEASUREMENT_ID, analyticsConfig);
          }

        } else {
          console.log('[Analytics] Firebase Analytics not supported in this environment');
        }
      });
    } else {
      console.log('[Analytics] Firebase Analytics measurement ID not provided');
    }
    
    // Initialize Messaging
    isSupported().then((messagingSupported) => {
      if (!messagingSupported) {
        console.log('Firebase Messaging is not supported');
      } else {
        messaging = getMessaging(app);
        console.log('Firebase Messaging initialized');

        // onMessage fires when a push arrives while the tab is active — OS shows no banner,
        // so we record interaction_type=opened (received, not tapped).
        onMessage(messaging, (payload) => {
          const messageUuid = payload.data?.message_uuid;
          if (messageUuid && localStorage.getItem('user_token')) {
            registerPushNotificationInteraction({
              message_uuid: messageUuid,
              interaction_type: 'opened',
              source: 'foreground',
              occurred_at: new Date().toISOString(),
            }).catch(() => {});
          }
        });
      }
    });
  } catch (err) {
    console.error('Failed to initialize Firebase', err);
  }
} else {
  console.log('Firebase not configured - push notifications disabled');
}

export const fetchToken = async () => {
  if (!isFirebaseConfigured()) {
    console.log('Firebase not configured - cannot fetch token');
    return null;
  }

  if (!messaging) {
    console.log('Firebase Messaging not initialized');
    return null;
  }

  if (!(await isSupported())) {
    console.log('Firebase Messaging is not supported');
    return null;
  }

  if (!APPLICATION_KEYS.publicKey) {
    console.log('VAPID public key not configured');
    return null;
  }

  return getToken(messaging, {vapidKey: APPLICATION_KEYS.publicKey}).then((currentToken) => {
    if (currentToken) {
      console.log('current token for client: ', currentToken);
      // Track the token -> client mapping, by sending to backend server
      // show on the UI that permission is secured
      return currentToken;
    } else {
      console.log('No registration token available. Request permission to generate one.');
      return null;
    }
  }).catch((err) => {
    console.log('An error occurred while retrieving token. ', err);
    // catch error while creating client token
    return null;
  });
}

export function askNotificationPermission() {
  if (!isFirebaseConfigured()) {
    console.log('Firebase not configured - notifications not available');
    return Promise.resolve(null);
  }

  // Use modern Promise-based API
  return Notification.requestPermission().then(function (permissionResult) {
    if (permissionResult !== "granted") {
      throw new Error("We weren't granted permission.");
    } else {
      console.log("Notification permission granted!");
      return fetchToken();
    }
  });
}

export const onMessageListener = () => {
  if (!isFirebaseConfigured() || !messaging) {
    console.log('Firebase not configured - message listener not available');
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    onMessage(messaging, (payload) => {
      resolve(payload);
    });
  });
};

// Export analytics instance for use in other components
export const getAnalyticsInstance = () => analytics;